import { useQuery } from '@tanstack/react-query';

import type { SupportedTimeZone } from '@/constants/date';
import type { AgentDetailResponse, OrgAnalyticsRequest } from '@/pages/analytics/types';
import { applyTrendBucketLabels, buildTrendBucketPlan } from '@/pages/analytics/utils/dateWindow';
import { analyticsService } from '@/services/api';
import { naiveDateToUtc } from '@/utils/date';
import { serializeMultiValueParam } from '@/utils/urlParams';

import { analyticsQueryKeys, QC_TREND_KEYS } from './useOrgAnalytics';

interface UseAgentDetailArgs {
  organizationId: string | null;
  agentId: string | null;
  programIds?: string[];
  callDirections?: string[];
  start?: string;
  end?: string;
  timeZone: SupportedTimeZone;
}

const getAgentDetail = async (
  agentId: string,
  params: OrgAnalyticsRequest,
): Promise<AgentDetailResponse> => {
  const scopedParams: OrgAnalyticsRequest = { ...params, agent_ids: agentId };
  const { organization_id, ...queryParams } = scopedParams;
  const [analytics, summary] = await Promise.all([
    analyticsService.getOrgAnalytics({ organization_id }, { params: queryParams }),
    analyticsService.getAgentsSummary({ organization_id }, { params: queryParams }),
  ]);
  // The generated proto type has by_metadata as {entries: T[]} wrappers, but the
  // HTTP handler flattens it to bare arrays, matching AgentDetailResponse at runtime.
  const typedAnalytics = analytics as unknown as Omit<AgentDetailResponse, 'agent'>;
  const typedSummary = summary as { agents: Array<{ agent_id: string; agent_name?: string }> };
  const agentRow = typedSummary.agents.find(a => a.agent_id === agentId);
  const agentName = agentRow?.agent_name;

  return {
    ...typedAnalytics,
    // Omit agent meta entirely when no real name resolves; the display layer
    // falls back to preserved metadata or the route agent id.
    agent: agentName ? { agent_id: agentId, agent_name: agentName } : undefined,
  };
};

export const useAgentDetail = ({
  organizationId,
  agentId,
  programIds,
  callDirections,
  start,
  end,
  timeZone,
}: UseAgentDetailArgs) => {
  const trendPlan = buildTrendBucketPlan(start, end, timeZone);
  const params: OrgAnalyticsRequest = {
    organization_id: organizationId ?? '',
    program_ids: programIds && programIds.length ? programIds.join(',') : undefined,
    call_directions: serializeMultiValueParam(callDirections),
    start: start ? naiveDateToUtc(start, timeZone, 'start') : undefined,
    end: end ? naiveDateToUtc(end, timeZone, 'end') : undefined,
    ...trendPlan?.params,
  };

  return useQuery({
    queryKey: [...analyticsQueryKeys.all, 'agents', 'detail', agentId, params] as const,
    queryFn: async () => {
      const detail = await getAgentDetail(agentId ?? '', params);
      // The backend echoes each bucket's UTC start as the trend date; relabel it
      // to the local YYYY-MM-DD the charts expect.
      return trendPlan
        ? applyTrendBucketLabels(detail, QC_TREND_KEYS, trendPlan.labelByStart)
        : detail;
    },
    enabled: !!organizationId && !!agentId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};
